'use client';

/**
 * ActionButton — one `vendor_actions` item rendered via the Button primitive.
 *
 * Shared by the overlay ProductActionsCard (horizontal strip) and the rail
 * VendorActionsCard (vertical stack) so the BFF-icon map, the primary/secondary
 * variant rule, and the Button primitive live in ONE place instead of being
 * hand-rolled per card. Layout (row vs stack, width, alignment) rides in
 * `className` from the caller.
 *
 * Behaviour is opt-in per caller via `onClick` — most actions
 * (create_rfq, view_catalog, send_message) are still unwired stubs; a
 * caller only passes `onClick` once it has real behaviour for that key.
 *
 * @layer presentation
 * @arch_component_type design-system
 */

import { FilePlus, Package, MessageSquare, Sparkles, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ActionItem {
  key: string;
  label: string;
  icon?: string;
  disabled?: boolean;
}

// The BFF ships lucide icon names (kebab-case) on each action. Map the known
// ones; Zap is the fallback so an unmapped icon still renders a button.
const ICONS: Record<string, LucideIcon> = {
  'file-plus': FilePlus,
  package: Package,
  'message-square': MessageSquare,
  sparkles: Sparkles,
};

export function ActionButton({
  action,
  className,
  onClick,
  loading,
}: {
  action: ActionItem;
  className?: string;
  onClick?: () => void;
  loading?: boolean;
}) {
  const Icon = (action.icon && ICONS[action.icon]) || Zap;
  // The headline action (create_rfq) is the primary CTA; everything else is
  // secondary (outline). One rule, both surfaces.
  const variant = action.key === 'create_rfq' ? 'default' : 'outline';
  return (
    <Button
      variant={variant}
      size="sm"
      disabled={action.disabled || loading}
      className={className}
      onClick={onClick}
    >
      <Icon />
      {action.label}
    </Button>
  );
}
